<template>
  <div class="col-sm-12">
    <div class="calendar-datarange-selector">
      <ul class="single-data mr-3 col-sm-12">
        <li class="mr-2 rounded bg-light-gray cursor-pointer float-left action">
          <a @click="goToPrevious()">
            <i class="fa fa-chevron-left" />
          </a>
        </li>
        <li
          class="ml-2 rounded bg-light-gray cursor-pointer float-right action"
        >
          <a @click="goToNext()">
            <i class="fa fa-chevron-right" />
          </a>
        </li>
        <li class="rounded bg-light-gray title">
          <div>
            {{ options[0] ? options[0].title : '' }}
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import calendarViewModes from '@/utils/shared/constants/calendarViewModes';
import moment from 'moment';

export default {
  name: 'ViewDataRangeSelector',
  props: {
    value: {
      type: Object,
      default: () => ({
        month: 0,
        week: 0,
        day: 0,
        startDate: null,
        endDate: null,
      }),
    },
    year: {
      type: Number,
      default: () => new Date().getFullYear(),
    },
    viewMode: {
      type: Number,
      default: () => calendarViewModes.Month,
    },
  },
  data() {
    return {
      minDate: null,
      maxDate: null,
      options: [],
      changePerformed: false,
    };
  },
  computed: {
    currentOptions() {
      return [
        { id: calendarViewModes.Month, name: 'Mes' },
        { id: calendarViewModes.Week, name: 'Semana' },
        { id: calendarViewModes.Day, name: 'Día' },
      ];
    },
    availableOptions() {
      return this.options;
    },
  },
  watch: {
    value(newValue) {
      let currentSelectedOption = { firstDate: newValue.startDate };

      if (this.viewMode == calendarViewModes.Month) {
        currentSelectedOption.id =
          currentSelectedOption.firstDate.getMonth() + 1;
      } else if (this.viewMode == calendarViewModes.Week) {
        //Change the start date based on the week number
        currentSelectedOption.firstDate = moment(
          currentSelectedOption.firstDate
        )
          .startOf('week')
          .toDate();

        currentSelectedOption.id = newValue.week;
      } else {
        currentSelectedOption.id = newValue.day;
      }

      this.optionSelected(currentSelectedOption);
    },
    viewMode() {
      let currentSelectedOption = {
        firstDate: this.changePerformed ? this.value.startDate : new Date(),
      };

      if (this.viewMode == calendarViewModes.Month) {
        currentSelectedOption.id =
          currentSelectedOption.firstDate.getMonth() + 1;
      } else if (this.viewMode == calendarViewModes.Week) {
        //Change the start date based on the week number
        currentSelectedOption.firstDate = moment(
          currentSelectedOption.firstDate
        )
          .startOf('week')
          .toDate();

        currentSelectedOption.id = this.value.week;
      } else {
        if (this.changePerformed) {
          currentSelectedOption.id = this.value.day;
        }
      }

      this.optionSelected(currentSelectedOption);
    },
  },
  mounted() {
    if (this.value.startDate == null) {
      this.setInitialOptions();
      this.updateOptions();
    }
  },
  methods: {
    setInitialOptions() {
      let currentDate = new Date();

      this.minDate = new Date(this.year - 1, 6, 1);
      this.maxDate = new Date(this.year, 6, 0);

      this.value.startDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );
      this.value.endDate = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );

      this.value.month = currentDate.getMonth() + 1;
      this.value.week = moment(currentDate).week();
      this.value.day = currentDate.getDate();

      this.$emit('on-datarange-changed', this.value);
    },
    goToPrevious() {
      this.changePerformed = true;

      let previouStartDate = null;

      if (this.viewMode == calendarViewModes.Month) {
        previouStartDate = moment(this.value.startDate)
          .add(-1, 'month')
          .toDate();
      } else if (this.viewMode == calendarViewModes.Week) {
        previouStartDate = moment(this.value.startDate)
          .startOf('week')
          .add(-1, 'week')
          .toDate();

        //Prevent the violation of the start date range
        if (previouStartDate < this.minDate) previouStartDate = this.minDate;
      } else {
        previouStartDate = moment(this.value.startDate)
          .add(-1, 'day')
          .toDate();
      }

      let previouEndDate = null;

      if (this.viewMode == calendarViewModes.Month) {
        previouEndDate = moment(this.value.endDate)
          .add(-1, 'month')
          .toDate();
      } else if (this.viewMode == calendarViewModes.Week) {
        if (this.minDate < this.value.endDate) {
          previouEndDate = moment(previouStartDate)
            .startOf('week')
            .add(1, 'week')
            .add(-1, 'day')
            .toDate();
        } else {
          previouEndDate = moment(this.value.endDate)
            .startOf('week')
            .add(-1, 'week')
            .toDate();
        }
      } else {
        previouEndDate = moment(this.value.endDate)
          .add(-1, 'day')
          .toDate();
      }

      if (previouStartDate >= this.minDate || previouEndDate >= this.minDate) {
        this.value.startDate = previouStartDate;
        this.value.endDate = previouEndDate;

        this.value.month = this.value.startDate.getMonth();
        this.value.week = moment(this.value.startDate).week();
        this.value.day = this.value.startDate.getDate();

        this.$emit('on-datarange-changed', this.value);

        this.updateOptions();
      }
    },
    goToNext() {
      this.changePerformed = true;

      let nextStartDate = null;

      if (this.viewMode == calendarViewModes.Month) {
        nextStartDate = moment(this.value.startDate)
          .add(1, 'month')
          .toDate();
      } else if (this.viewMode == calendarViewModes.Week) {
        nextStartDate = moment(this.value.startDate)
          .startOf('week')
          .add(1, 'week')
          .toDate();
      } else {
        nextStartDate = moment(this.value.startDate)
          .add(1, 'day')
          .toDate();
      }

      if (nextStartDate <= this.maxDate) {
        if (this.viewMode == calendarViewModes.Month) {
          this.value.startDate = nextStartDate;
          this.value.endDate = moment(this.value.startDate)
            .add(1, 'month')
            .add(-1, 'day')
            .toDate();
        } else if (this.viewMode == calendarViewModes.Week) {
          this.value.startDate = nextStartDate;

          this.value.endDate = moment(this.value.startDate)
            .add(1, 'week')
            .add(-1, 'day')
            .toDate();

          //Prevent the violation of the end date range
          if (this.value.endDate > this.maxDate)
            this.value.endDate = this.maxDate;
        } else {
          this.value.startDate = moment(this.value.startDate)
            .add(1, 'day')
            .startOf('day')
            .toDate();
          this.value.endDate = moment(this.value.startDate)
            .endOf('day')
            .toDate();
        }

        this.value.month = this.value.startDate.getMonth();
        this.value.week = moment(this.value.startDate).week();
        this.value.day = this.value.startDate.getDate();

        this.$emit('on-datarange-changed', this.value);

        this.updateOptions();
      }
    },
    updateOptions() {
      this.options = [];

      if (this.viewMode == calendarViewModes.Month) {
        let currentDate = new Date(this.value.startDate.getTime());

        this.options.push({
          id: currentDate.getMonth() + 1,
          title:
            moment(currentDate)
              .format('MMMM')
              .toUpperCase() +
            ' ' +
            moment(currentDate).format('YYYY'),
          firstDate: new Date(currentDate.getTime()),
        });

        currentDate.setMonth(currentDate.getMonth() + 1);
      } else if (this.viewMode == calendarViewModes.Week) {
        //Prevent the violation of the end date range
        if (this.value.startDate < this.minDate)
          this.value.startDate = this.minDate;

        let startMonthName = moment(this.value.startDate).format('MMM');
        let startDay = this.value.startDate.getDate();
        let startYear = this.value.startDate.getFullYear();

        //Prevent the violation of the end date range
        if (this.value.endDate > this.maxDate)
          this.value.endDate = this.maxDate;

        this.options.push({
          id: this.value.week,
          title: `${startDay} de ${startMonthName} de ${startYear}`,
          firstDate: new Date(this.value.startDate.getTime()),
        });
      } else {
        let startMonthName = moment(this.value.startDate).format('MMMM');
        let startDay = this.value.startDate.getDate();
        let startYear = this.value.startDate.getFullYear();

        const today = new Date();
        const isToday =
          this.value.startDate.getDate() === today.getDate() &&
          this.value.startDate.getMonth() === today.getMonth() &&
          this.value.startDate.getFullYear() === today.getFullYear();

        this.options.push({
          id: this.value.day,
          title: `${
            isToday ? 'Hoy, ' : ''
          } ${startDay} de ${startMonthName} de ${startYear}`,
          firstDate: new Date(this.value.startDate.getTime()),
        });
      }
    },
    optionSelected(item) {
      let currentMonth = 0;
      let currentWeek = 0;
      let currentDay = 0;

      if (this.viewMode == calendarViewModes.Month) {
        currentWeek = moment(item.firstDate).week();
        currentMonth = item.id;
        currentDay = 1;

        this.value.startDate = item.firstDate;
        this.value.endDate = new Date(
          item.firstDate.getFullYear(),
          item.firstDate.getMonth() + 1,
          0
        );
      } else if (this.viewMode == calendarViewModes.Week) {
        (currentWeek = item.id), (currentMonth = item.firstDate.getMonth() + 1);
        currentDay = item.firstDate.getDate();

        this.value.startDate = item.firstDate;
        this.value.endDate = moment(item.firstDate)
          .add(1, 'week')
          .add(-1, 'day')
          .toDate();
      } else {
        currentWeek = moment(item.firstDate).week();
        currentMonth = item.firstDate.getMonth() + 1;
        currentDay = item.id;

        this.value.startDate = moment(item.firstDate)
          .startOf('day')
          .toDate();
        this.value.endDate = moment(item.firstDate)
          .endOf('day')
          .toDate();
      }

      this.value.month = currentMonth;
      this.value.week = currentWeek;
      this.value.day = currentDay;

      this.$emit('on-datarange-changed', this.value);

      this.updateOptions();
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/shared/scss/config/_colors.scss';

.calendar-datarange-selector {
  margin-top: 30px;

  .multi-data {
    float: left;
    line-height: 16px;
    text-align: center;
    padding: 5px 0px;
    cursor: pointer;

    &.active {
      border: 2px solid map-get($colors, 'orange');
      border-radius: 8px;
    }

    &:hover {
      border-radius: 8px;
      background: map-get($colors, 'orange');
      color: map-get($colors, 'white');
    }
  }

  .single-data {
    font: Bold 14px/17px Lato;
    padding: 0px 0px;
  }

  .single-data > li {
    list-style-type: none !important;

    &.action {
      width: 33px;
      height: 34px;
    }
  }

  .single-data .title {
    height: 34px;
    text-align: center;
    padding-top: 8px;
    overflow: hidden;
  }

  .single-data > li > a {
    min-width: 33px;
    position: relative;
    float: left;
    text-align: center;
    line-height: 34px;

    letter-spacing: 0;
    color: #11291b;
    text-decoration: none;
  }
  .single-data > ul > li {
    background: #f2f2f2 0% 0% no-repeat padding-box;
    border: 1px solid #e2e2e2;
    border-radius: 4px;
  }

  .rounded {
    border-radius: 4px;
  }
}

.subtitle {
  font-weight: normal;
  font-size: 12px;
}
</style>
